// Cómo va un pedido: el estado con su color y los cuatro pasos que ve el cliente (Recibido → Confirmado → En camino →
// Entregado). Los colores del estado son fijos (verde, celeste, ámbar, rojo): no dependen de la marca, para que
// "Entregado" nunca salga en rojo en una empresa cuyo color es rojo.
import { PASOS_SEGUIMIENTO, type Seguimiento } from '../compartido';
import { cx, Icono } from '@reparto/ui';

const TONO: Record<Seguimiento['tono'], { chip: string; icono: string }> = {
  espera: { chip: 'bg-amber-100 text-amber-900', icono: 'schedule' },
  curso: { chip: 'bg-sky-100 text-sky-900', icono: 'local_shipping' },
  ok: { chip: 'bg-emerald-100 text-emerald-900', icono: 'check_circle' },
  error: { chip: 'bg-rose-100 text-rose-900', icono: 'error' },
  neutro: { chip: 'bg-stone-200 text-stone-800', icono: 'block' },
};

/** Íconos por estado cuando el del tono no dice lo justo (un pedido confirmado todavía no va en camino). */
const ICONO_CLAVE: Partial<Record<Seguimiento['clave'], string>> = {
  confirmado: 'task_alt',
  preparado: 'inventory_2',
  entregado_parcial: 'rule',
  cancelado: 'block',
};

export function EstadoPedido({ s, grande }: { s: Seguimiento; grande?: boolean }) {
  const t = { ...TONO[s.tono], icono: ICONO_CLAVE[s.clave] ?? TONO[s.tono].icono };
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 rounded-full font-semibold whitespace-nowrap',
        grande ? 'px-3.5 py-1.5 text-base' : 'px-3 py-1 text-sm',
        t.chip,
      )}
    >
      <Icono nombre={t.icono} tamano={grande ? 20 : 18} relleno />
      {s.etiqueta}
    </span>
  );
}

export type EstadoPaso = 'hecho' | 'actual' | 'fallo' | 'pendiente';

/**
 * `paso` es el último alcanzado y está hecho, salvo "En camino", que dura hasta la entrega y queda resaltado. Esperando
 * la confirmación, lo resaltado es "Confirmado". Si el pedido se cortó (no aprobado, cancelado, no entregado), el paso
 * siguiente es el que falló.
 */
export function estadoPaso(n: number, s: Pick<Seguimiento, 'paso' | 'tono'>): EstadoPaso {
  const cortado = s.tono === 'error' || s.tono === 'neutro';
  if (n < s.paso) return 'hecho';
  if (n === s.paso) return s.tono === 'curso' && n === 3 ? 'actual' : 'hecho';
  if (n === s.paso + 1) {
    if (cortado) return 'fallo';
    if (s.tono === 'espera') return 'actual';
  }
  return 'pendiente';
}

export function Pasos({ s }: { s: Seguimiento }) {
  const gris = s.tono === 'neutro';
  return (
    <ol className="grid grid-cols-4" aria-label={`Seguimiento: ${s.etiqueta}`}>
      {PASOS_SEGUIMIENTO.map((texto, i) => {
        const n = i + 1;
        const e = estadoPaso(n, s);
        return (
          <li
            key={texto}
            className="relative flex flex-col items-center text-center"
            aria-current={e === 'actual' ? 'step' : undefined}
          >
            {i > 0 ? (
              <span
                aria-hidden="true"
                className={cx(
                  'absolute top-[18px] right-1/2 h-1 w-full -translate-y-1/2',
                  e === 'hecho' || (e === 'actual' && n <= s.paso)
                    ? 'bg-emerald-600'
                    : e === 'fallo' && !gris
                      ? 'bg-rose-300'
                      : 'bg-stone-200',
                )}
              />
            ) : null}
            <span
              className={cx(
                'relative z-10 grid h-9 w-9 place-items-center rounded-full border-2 text-sm font-bold',
                e === 'hecho' && 'border-emerald-600 bg-emerald-600 text-white',
                e === 'actual' && 'border-emerald-600 bg-white text-emerald-700 ring-4 ring-emerald-100',
                e === 'fallo' &&
                  (gris ? 'border-stone-500 bg-stone-500 text-white' : 'border-rose-600 bg-rose-600 text-white'),
                e === 'pendiente' && 'border-stone-300 bg-white text-stone-500',
              )}
            >
              {e === 'hecho' ? (
                <Icono nombre="check" tamano={20} />
              ) : e === 'fallo' ? (
                <Icono nombre="close" tamano={20} />
              ) : e === 'actual' ? (
                <Icono nombre={n === 3 ? 'local_shipping' : 'schedule'} tamano={20} />
              ) : (
                n
              )}
            </span>
            <span
              className={cx(
                'mt-1.5 px-0.5 text-[13px] leading-tight',
                e === 'pendiente' ? 'text-stone-500' : 'font-semibold text-stone-900',
                e === 'fallo' && !gris && 'text-rose-800',
              )}
            >
              {texto}
              <span className="sr-only">
                {e === 'hecho'
                  ? ' (listo)'
                  : e === 'actual'
                    ? ' (ahora)'
                    : e === 'fallo'
                      ? ' (no se hizo)'
                      : ' (falta)'}
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
